import React, { Component, ErrorInfo, ReactNode } from "react"
import Button from "./ui/Button"
import * as Sentry from "@sentry/react"
import { captureMessage } from "@sentry/core/build/types/exports"
import { ArrowPathIcon } from "@heroicons/react/20/solid"

interface Props {
  children: ReactNode
  ctaLabel?: string
  description?: string
  ctaOnClick?: () => void
}

interface State {
  hasError: boolean
  error: Error | undefined
  errorMessage: string | undefined
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: undefined,
    errorMessage: undefined,
  }

  public static getDerivedStateFromError(err: Error): State {
    return { hasError: true, error: err, errorMessage: err.message }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo)
    Sentry.withScope((scope) => {
      scope.setExtras({ errorInfo, errorMessage: error.message })
      scope.setTag("parkType", "app-breaking")
      Sentry.captureException(error)
    })
  }

  public refreshPage() {
    window.location.reload()
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className='fixed inset-0 bg-black bg-opacity-40 backdrop-blur-[1.5px] flex items-center justify-center z-50'>
          <div className='bg-white p-6 rounded-lg flex flex-col items-center justify-center gap-5'>
            <h1 className='text-3xl'>🏕️😓</h1>
            <h1 className='text-2xl'>Uh-oh! We've encountered an error</h1>
            <h2 className='text-lg max-w-xl text-center'>
              {this.props.description ? this.props.description : "Please refresh the page."} If you run into this issue again, please notify us at{' '}
              <a
                href={`mailto:support@poweredbypark.com?subject=Error%20Report&body=Please share any additional context if relevant about how you encountered this:%0A%0A%0AThis error information below will help our team when getting this fixed for you. Thank you!%0A%0AError:%20${encodeURIComponent(
                  this.state.errorMessage || "",
                )}%0A%0AInfo:%20${encodeURIComponent(this.state.error?.stack || "")}`}
                className="underline"
              >
                support@poweredbypark.com
              </a>
            </h2>
            <Button
              className='w-40 mt-4 mb-2'
              onClick={this.props.ctaOnClick ? this.props.ctaOnClick : this.refreshPage}
              icon={!this.props.ctaLabel && <ArrowPathIcon className='h-5 w-5 text-gray-700' />}>
              {this.props.ctaLabel ? this.props.ctaLabel : "Refresh"}
            </Button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
